import { useLocaleState } from "ra-core";
import { useMemo } from "react";

import {
  formatDate,
  formatDateTime,
  formatRelative,
  formatRelativeDay,
  formatTime,
  toIntlLocale,
  type DateValue,
} from "@/lib/dates";

/**
 * The `@/lib/dates` helpers bound to the current app locale, e.g.
 * `const { formatDateTime } = useDateFormat(); formatDateTime(task.due_date)`.
 */
export const useDateFormat = () => {
  const [locale = "es"] = useLocaleState();
  return useMemo(
    () => ({
      locale,
      intlLocale: toIntlLocale(locale),
      formatDate: (value: DateValue, options?: Intl.DateTimeFormatOptions) =>
        formatDate(value, locale, options),
      formatDateTime: (
        value: DateValue,
        options?: Intl.DateTimeFormatOptions,
      ) => formatDateTime(value, locale, options),
      formatTime: (value: DateValue) => formatTime(value, locale),
      formatRelative: (value: DateValue) => formatRelative(value, locale),
      formatRelativeDay: (value: DateValue) => formatRelativeDay(value, locale),
    }),
    [locale],
  );
};

export type DateFormat = ReturnType<typeof useDateFormat>;
